/* eslint-disable max-lines */
import _ from 'lodash';
import { action, computed, observable } from 'mobx';
import type { ImageFile } from 'react-dropzone';
import moment from 'moment-timezone';
import { compactObject } from '@this/src/util/object/object.util';
import { Fetcher, toDomesticTel } from '@this/src/util';
import {
  getMonthsUntilExpire,
  isOverDate,
  isOverLimitDateHour,
  isWithinOneYear
} from '@this/shared/checker/date_checker';
import type TripApproveItemJson from '@this/src/domain/trip_approve_item/trip_approve_item_json';
import User, { convertUserResponseToArgs } from './user/user2';
import ReservingTrip, { convertReservingTripResponseToArgs } from './trip/reserving_trip2';
import Department, { convertDepartmentResponseToArgs } from './department/department2';
import type OrganizationAddress from './department/organization_address/organization_address2';
import type OrganizationBase from './organization_base/organization_base2';
import type { ShinkansenSeatType, TravelerInformationResponseArgs } from './traveler_information';
import TravelerInformation from './traveler_information';
import type { OrderItemMappingMode } from './order_item_mapping2';
import OrderItemMapping from './order_item_mapping2';
import ApproveItem, {
  convertApproveItemResponseToArgs,
  type ApproveItemResponseArgs
} from './approve_item/approve_item2';
import BakurakuWorkflowRequest, {
  convertBakurakuWorkflowRequestResponseToArgs,
  formatBakurakuWorkflowRequestLabel,
  type BakurakuWorkflowRequestResponseArgs
} from './bakuraku_workflow_request';
import type { BakurakuWorkflowRequirementMode } from './organization/organization2';
import type { PaymentGatewayType, PaymentMethodOptions, PaymentMethodType } from './organization/organization3';
import type { WorkflowStyle } from './workflow_style';
import type RentalCarLimitType from './organization/rental_car_limit_type';
import Hotel from './hotel/hotel2';
import type { UserResponseArgs } from './user/user2';
import type { SelectType } from './select_store';
import type { ReservingTripItemResponseArgs } from './trip/reserving_trip_item2';
import { resolveRentalCarDriverTel, type RentalCarResponseArgs } from './rental_car';
import type Grade from './grade2';
import type { DepartmentResponseArgs } from './department/department2';
import type { ChargingDepartmentShareResponseArgs } from './department/charging_department_share2';
import ChargingDepartmentShare, {
  convertChargingDepartmentShareResponseToArgs
} from './department/charging_department_share2';
import type { FilterableSelectorOption } from '../components/shared/filterable_selector/filterable_selector';
import type { ExpensesAccountTypeResponseArgs } from './expenses/expenses_account_type2';
import ExpensesAccountType, { convertExpensesAccountTypeResponseToArgs } from './expenses/expenses_account_type2';
import type ReservingTripItem from './trip/reserving_trip_item2';
import type NonOrderItem from './non_order_item2';
import type { ProjectResponseArgs } from './project/project2';
import type { ProjectShareResponseArgs } from './project/project_share2';
import Project, { convertProjectResponseToArgs } from './project/project2';
import ProjectShare, { convertProjectShareResponseToArgs } from './project/project_share2';
import type { ConsignmentSaleResponseArgs } from './consignment_sale';
import ConsignmentSale, { convertConsignmentSaleResponseToArgs } from './consignment_sale';
import type { TripDuplicationResponseArgs } from './trip_duplication';
import TripDuplication, { convertTripDuplicationResponseToArgs } from './trip_duplication';
import type { MarginTypeResponseArgs } from './organization/margin_type3';
import type { ApproverArgs } from './approver/approver';
import ApproverList from './approver/approver_list';

export interface PaymentTransaction {
  token?: string;
  uid: string;
}

export interface PaymentGatewayMember {
  uid: string;
  name: string;
  masked_credit_card_number: string;
}

export interface TabiregiConsentState {
  consented: boolean;
  registeredAt: string | null;
}

export type ShinkansenAddressType = 'select' | 'text' | 'organization_base_select';
export type OrderItemMappingListTarget = 'department' | 'project' | 'expenses_account_type';
export interface ApproveItemsResponse {
  approve_items: (ApproveItemResponseArgs & {
    default_item?: number | null;
    default_item_code?: string | null;
    default_item_name?: string | null;
  })[];
  total_page: number;
}

interface Stage {
  stage: number;
  approvers: (User | null)[];
}

const buildApproveStagesPayload = (stages: Stage[]) =>
  stages
    .map(({ stage, approvers }) => ({
      stage,
      approvers: approvers
        .filter((approver): approver is User => !!approver)
        .map(approver => ({
          id: approver.id,
          name: approver.name,
          lastName: approver.lastName,
          firstName: approver.firstName,
          lastNameRoman: approver.lastNameRoman,
          firstNameRoman: approver.firstNameRoman,
          lastNameKana: approver.lastNameKana,
          firstNameKana: approver.firstNameKana,
          birthday: approver.birthday?.toJSON?.() ?? null,
          gender: approver.gender,
          email: approver.email,
          tel: approver.tel,
          nationalityId: approver.nationalityId,
          nationalities: approver.nationalities,
          department: {},
          domesticApprovalRequired: approver.domesticApprovalRequired,
          foreignApprovalRequired: approver.foreignApprovalRequired,
          passportNumber: approver.passportNumber,
          passportExpire: approver.passportExpire,
          mileageNumbers: approver.mileageNumbers,
          userMemos: approver.userMemos,
          adminNotification: approver.adminNotification,
          autoSyncTripToCalendar: approver.autoSyncTripToCalendar,
          aiTravelSlackToken: approver.aiTravelSlackToken,
          exicId: approver.exicId,
          exicPassword: approver.exicPassword,
          exicCardId: approver.exicCardId,
          exicCards: approver.exicCards,
          userForwardedEmails: approver.userForwardedEmails,
          userNotificationSettings: approver.userNotificationSettings ?? []
        }))
    }))
    .filter(({ approvers }) => approvers.length > 0);

interface HotelInfo {
  id: string | null | undefined;
  name: string | null | undefined;
  type: string;
  rooms:
    | {
        hotelFirstName: string;
        hotelLastName: string;
        hotelFirstNameKana?: string;
        hotelLastNameKana?: string;
        hotelBirthday: string;
        hotelGender: string;
        hotelTel: string;
        bedTypes: { id: number; description: string }[] | null | undefined;
        bedTypeId: number | null | undefined;
        bedTypeDescription: string | null | undefined;
      }[]
    | null
    | undefined;
  plan_name: string | null | undefined;
}

interface PassportWarningTraveler {
  userId: number | null;
  passportExpire: string;
}

interface PassportWarningTravelerForUi extends PassportWarningTraveler {
  name: string;
  remainingMonths: number;
}

export interface DefaultWorkflowApprovers {
  [key: string]: { id: number; name: string }[];
}

interface BakurakuWorkflowRequestsResponse {
  bakuraku_workflow_requests: BakurakuWorkflowRequestResponseArgs[];
}

export type ReserveInfoResponseArgs = {
  user: UserResponseArgs;
  type?: SelectType;
  items: ReservingTripItemResponseArgs[];
  travelers: TravelerInformationResponseArgs[];
  shinkansen_address_type?: ShinkansenAddressType;
  shinkansen_address?: string;
  shinkansen_addressee?: string;
  postcode?: string;
  all_travelers_notification?: boolean;
  package_type?: string;
  package_search_condition?: {
    select_page_url?: string;
  };
  roomnum?: number | string;
  rental_cars?: RentalCarResponseArgs[];
  domestic_exist?: boolean;
  foreign_exist?: boolean;
  payment_gateway_type?: PaymentGatewayType;
  payment_gateway_members?: PaymentGatewayMember[];
  has_members?: boolean;
  is_simple_request?: boolean;
  show_fee?: boolean;
  login_user_id?: number;
  nationalities: { id: number; name: string }[];
  rentalcar_available?: boolean;
  car_type: [string, string][];
  default_workflow_approvers?: DefaultWorkflowApprovers;
  use_default_workflow?: boolean;
  use_charging_department_workflow?: boolean;
  workflow_style?: WorkflowStyle;
  concur_expense_enabled?: boolean;
  concur_connected?: boolean;
  tabiregi_consent?: {
    consented: boolean;
    registered_at: string | null;
  };
  departments: DepartmentResponseArgs[];
  charging_department_shares: ChargingDepartmentShareResponseArgs[];
  expenses_account_types: ExpensesAccountTypeResponseArgs[];
  project_share_availability?: boolean;
  projects: ProjectResponseArgs[];
  project_shares: ProjectShareResponseArgs[];
  package_provider: string;
  consignment_sale: ConsignmentSaleResponseArgs;
  trip_duplications?: TripDuplicationResponseArgs[];
  margin_types: MarginTypeResponseArgs[];
  airline?: 'ANA' | 'JAL';
  approvers?: ApproverArgs[];
  search_query_id?: number;
  expenses_account_type_available?: boolean;
  bakuraku_workflow_integration_enabled?: boolean;
  bakuraku_workflow_requirement_mode?: BakurakuWorkflowRequirementMode | null;
};

export type ReserveInfoArgs = {
  user: User;
  type?: SelectType;
  reservingTrip: ReservingTrip;
  shinkansenAddressType?: 'select' | 'text' | 'organization_base_select';
  shinkansenAddress?: string;
  shinkansenAddressee?: string;
  postcode?: string;
  allTravelersNotification?: boolean;
  packageType?: string;
  roomNum?: number;
  packageSearchCondition?: {
    selectPageUrl?: string;
  };
  domesticExist?: boolean;
  foreignExist?: boolean;
  paymentGatewayType?: PaymentGatewayType;
  rentalCarLimitType?: RentalCarLimitType;
  workflowStyle?: WorkflowStyle;
  useDefaultWorkflow?: boolean;
  expensesAccountTypeAvailable?: boolean;
  paymentTransaction?: PaymentTransaction;
  paymentGatewayMembers?: PaymentGatewayMember[];
  hasMembers?: boolean;
  isSimpleRequest?: boolean;
  loginUserId?: number;
  nationalities: { id: number; name: string }[];
  rentalCarAvailable?: boolean;
  defaultWorkflowApprovers?: DefaultWorkflowApprovers;
  departments?: Department[];
  chargingDepartmentShares?: ChargingDepartmentShare[];
  expensesAccountTypes?: ExpensesAccountType[];
  projectShareAvailability?: boolean;
  projects?: Project[];
  projectShares?: ProjectShare[];
  packageProvider?: string;
  consignmentSale?: ConsignmentSale;
  tripDuplications?: TripDuplication[];
  approvers?: ApproverArgs[];
  useChargingDepartmentWorkflow?: boolean;
  concurExpenseEnabled?: boolean;
  concurConnected?: boolean;
  tabiregiConsent?: TabiregiConsentState;
  bakurakuWorkflowIntegrationEnabled?: boolean;
  bakurakuWorkflowRequirementMode?: BakurakuWorkflowRequirementMode | null;
  bakurakuWorkflowRequest?: BakurakuWorkflowRequest | null;
  searchQueryId?: number;
  preferredTicketingMethod?: 'qr' | 'postal' | null;
};

// TODO: 本番はBugSnagに送りたい
const isConvertLogEnabled = () => {
  if (process.env.NODE_ENV !== 'production') {
    return true;
  }
  if (typeof location === 'undefined') {
    return false;
  }
  const host = location.hostname.toLowerCase();
  return host.includes('stg');
};

const logReserveInfoConvertError = (error: unknown, response: ReserveInfoResponseArgs) => {
  if (!isConvertLogEnabled()) {
    return;
  }
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;
  console.error('[TypeConvertError]', {
    converter: 'ReserveInfo',
    api: '/reserve_confirm/show.json',
    message,
    stack
  });
};

export const convertReserveInfoResponseToArgs = (response: ReserveInfoResponseArgs) => {
  try {
    const responsePeopleNum = response.travelers?.length;
    const args: ReserveInfoArgs = {
      user: new User(convertUserResponseToArgs(response.user)),
      type: response.type,
      reservingTrip: new ReservingTrip(
        convertReservingTripResponseToArgs({
          type: response.type,
          items: response.items,
          rental_cars: response.rental_cars,
          car_type: response.car_type,
          travelers: response.travelers,
          margin_types: response.margin_types,
          airline: response.airline,
          show_fee: response.show_fee,
          peoplenum: responsePeopleNum
        })
      ),
      shinkansenAddressType: response.shinkansen_address_type,
      shinkansenAddress: response.shinkansen_address,
      shinkansenAddressee: response.shinkansen_addressee,
      postcode: response.postcode,
      allTravelersNotification: response.all_travelers_notification,
      packageType: response.package_type,
      roomNum:
        typeof response.roomnum === 'number'
          ? response.roomnum
          : response.roomnum
          ? parseInt(response.roomnum, 10)
          : undefined,
      packageSearchCondition: { selectPageUrl: response.package_search_condition?.select_page_url },
      domesticExist: response.domestic_exist,
      foreignExist: response.foreign_exist,
      paymentGatewayType: response.payment_gateway_type,
      paymentGatewayMembers: response.payment_gateway_members,
      hasMembers: response.has_members,
      isSimpleRequest: response.is_simple_request,
      loginUserId: response.login_user_id,
      nationalities: response.nationalities,
      rentalCarAvailable: response.rentalcar_available,
      workflowStyle: response.workflow_style,
      defaultWorkflowApprovers: response.default_workflow_approvers,
      concurExpenseEnabled: response.concur_expense_enabled,
      concurConnected: response.concur_connected,
      tabiregiConsent: response.tabiregi_consent
        ? {
            consented: response.tabiregi_consent.consented,
            registeredAt: response.tabiregi_consent.registered_at
          }
        : undefined,
      useDefaultWorkflow: response.use_default_workflow,
      departments: response.departments?.map(d => new Department(convertDepartmentResponseToArgs(d))),
      chargingDepartmentShares: response.charging_department_shares?.map(
        s => new ChargingDepartmentShare(convertChargingDepartmentShareResponseToArgs(s))
      ),
      expensesAccountTypes: (response.expenses_account_types ?? []).map(
        e => new ExpensesAccountType(convertExpensesAccountTypeResponseToArgs(e))
      ),
      projectShareAvailability: response.project_share_availability,
      projects: response.projects?.map(p => new Project(convertProjectResponseToArgs(p))),
      projectShares: response.project_shares?.map(s => new ProjectShare(convertProjectShareResponseToArgs(s))),
      packageProvider: response.package_provider,
      consignmentSale: new ConsignmentSale(convertConsignmentSaleResponseToArgs(response.consignment_sale)),
      tripDuplications: response.trip_duplications?.map(
        t => new TripDuplication(convertTripDuplicationResponseToArgs(t))
      ),
      approvers: response.approvers,
      useChargingDepartmentWorkflow: response.use_charging_department_workflow,
      searchQueryId: response.search_query_id,
      expensesAccountTypeAvailable: response.expenses_account_type_available,
      bakurakuWorkflowIntegrationEnabled: response.bakuraku_workflow_integration_enabled,
      bakurakuWorkflowRequirementMode: response.bakuraku_workflow_requirement_mode,
      preferredTicketingMethod:
        response.user?.organization?.use_postal && !response.user?.organization?.use_qr
          ? 'postal'
          : response.user?.organization?.use_qr || response.user?.organization?.use_postal
          ? 'qr'
          : null
    };
    return args;
  } catch (error) {
    logReserveInfoConvertError(error, response);
    throw error;
  }
};

// componentから動的に生成するのでResponseArgsやconverterは不要
export default class ReserveInfo {
  @observable user: User;
  @observable reservingTrip: ReservingTrip;
  @observable shinkansenAddressType: 'select' | 'text' | 'organization_base_select';
  @observable shinkansenAddress: string;
  @observable shinkansenAddressee: string;
  @observable organizationAddress?: OrganizationAddress;
  @observable organizationBase: OrganizationBase | null;
  @observable postcode: string;
  @observable allTravelersNotification: boolean;
  @observable notifiedUsers: (User | undefined)[];
  @observable internalNumber: string;
  @observable finalDestination: string;
  @observable purpose: string;
  @observable searchQueryId: number | undefined;
  @observable chargingDepartment: Department | null;
  @observable projectId: number | undefined;
  @observable expensesAccountTypeId: number | undefined;
  @observable orderItemMappingsProjectPart: OrderItemMapping[];
  @observable orderItemMappingsDepartmentPart: OrderItemMapping[];
  @observable orderItemMappingsExpensesPart: OrderItemMapping[];
  @observable showDepartmentDetail: boolean;
  @observable showProjectDetail: boolean;
  @observable showExpensesAccountTypeDetail: boolean;
  @observable type: string | undefined;
  @observable packageType: string | undefined;
  @observable roomNum: number | undefined;
  @observable packageSearchCondition?: { selectPageUrl?: string };
  @observable rentalCarRequired: 'true' | 'false';
  @observable privateUse: boolean;
  @observable approveStages: Stage[];
  @observable hotels: HotelInfo[];
  @observable seat?: ShinkansenSeatType;
  @observable ticket: 'normal' | 'kyuusyuu' | 'bulk_ticket' | 'ex' | undefined;
  @observable preferredTicketingMethod: 'qr' | 'postal' | null;
  @observable isSeparateShinkansenType: boolean;
  @observable domesticExist?: boolean;
  @observable foreignExist?: boolean;
  @observable approveItems: ApproveItem[];
  private approveItemsPath: string;
  @observable paymentGatewayType?: PaymentGatewayType;
  @observable paymentTransaction?: PaymentTransaction;
  @observable paymentMethodType?: PaymentMethodOptions; // organizationsからもらってくる選択肢
  @observable paymentMethod?: PaymentMethodType; // 今回の旅程に適用する支払方法
  @observable cardNum01: string;
  @observable cardNum02: string;
  @observable cardNum03: string;
  @observable cardNum04: string;
  @observable cardExpireYear: string;
  @observable cardExpireMonth: string;
  @observable cardCode: string;
  @observable cardName: string;
  @observable rentalCarLimitType: RentalCarLimitType | undefined;
  @observable workflowStyle: WorkflowStyle | undefined;
  @observable useDefaultWorkflow: boolean;
  @observable expensesAccountTypeAvailable: boolean;
  @observable paymentGatewayMembers: PaymentGatewayMember[] | undefined;
  @observable hasMembers?: boolean;
  @observable isSimpleRequest: boolean;
  @observable loginUserId?: number;
  @observable nationalities: { id: number; name: string }[];
  @observable rentalCarAvailable?: boolean;
  @observable departmentDetailMode: OrderItemMappingMode;
  @observable projectDetailMode: OrderItemMappingMode;
  @observable expensesAccountTypeDetailMode: OrderItemMappingMode;
  @observable defaultWorkflowApprovers?: DefaultWorkflowApprovers;
  @observable departments: Department[];
  @observable chargingDepartmentShares: ChargingDepartmentShare[];
  @observable expensesAccountTypes: ExpensesAccountType[];
  @observable projectShareAvailability?: boolean;
  @observable projects: Project[];
  @observable projectShares: ProjectShare[];
  @observable approveItemValues: Map<number, string>;
  @observable approveItemValueCodes: Map<number, string>;
  @observable approveItemFiles: Map<number, ImageFile>;
  @observable approveItemFilesBase64: Map<number, string>;
  @observable useChargingDepartmentWorkflow: boolean;
  @observable approvers?: ApproverArgs[];
  @observable concurExpenseEnabled: boolean;
  @observable concurConnected: boolean;
  @observable tabiregiConsent: TabiregiConsentState;
  @observable bakurakuWorkflowIntegrationEnabled: boolean;
  @observable bakurakuWorkflowRequirementMode: BakurakuWorkflowRequirementMode | null;
  @observable bakurakuWorkflowRequest: BakurakuWorkflowRequest | null;
  @observable packageProvider?: string;
  @observable consignmentSale?: ConsignmentSale;
  @observable termsAgree: boolean;
  @observable tripDuplications: TripDuplication[];
  @observable tripDuplicationAgree: boolean;
  @observable restrictedHotelAgree: boolean;
  @observable passportWarningTravelers: PassportWarningTraveler[];
  @observable passportWarningAgree: boolean;
  @observable showValidationErrors: boolean;

  constructor(args: ReserveInfoArgs) {
    this.user = args.user;
    this.reservingTrip = args.reservingTrip;
    this.reservingTrip.parentReserveInfo = this;
    this.shinkansenAddressType = args.shinkansenAddressType || 'select';
    this.shinkansenAddress = args.shinkansenAddress || '';
    this.shinkansenAddressee = args.shinkansenAddressee || '';
    this.organizationAddress = undefined;
    this.organizationBase = null;
    this.postcode = args.postcode || '';
    this.allTravelersNotification = args.allTravelersNotification != null ? args.allTravelersNotification : true;
    this.notifiedUsers = [];
    this.internalNumber = '';
    this.finalDestination = '';
    this.purpose = '';
    this.searchQueryId = args.searchQueryId;
    this.chargingDepartment = null;
    this.projectId = undefined;
    this.expensesAccountTypeId = undefined;
    this.orderItemMappingsProjectPart = [
      new OrderItemMapping({ travelerId: -1, orderItemType: 'all', orderItemIndex: -1 })
    ];
    this.orderItemMappingsDepartmentPart = [
      new OrderItemMapping({ travelerId: -1, orderItemType: 'all', orderItemIndex: -1 })
    ];
    this.orderItemMappingsExpensesPart = [
      new OrderItemMapping({ travelerId: -1, orderItemType: 'all', orderItemIndex: -1 })
    ];
    this.showDepartmentDetail = false;
    this.showProjectDetail = false;
    this.showExpensesAccountTypeDetail = false;
    this.type = args.type;
    this.packageType = args.packageType;
    this.roomNum = args.roomNum;
    this.packageSearchCondition = args.packageSearchCondition;
    const hasExistingRentalCars = this.reservingTrip.rentalCars.length > 0;
    this.rentalCarRequired = hasExistingRentalCars ? 'true' : 'false';
    this.privateUse = false;
    this.approveStages = [];
    // reserve_info.ts ではホテル入力用の rooms を ReservingTrip から初期化していた。
    // 2系移行時に初期化が抜けると、宿泊セクションは見出しのみ表示され部屋入力が描画されない。
    this.hotels = this.buildHotelsFromReservingTrip();
    this.isSeparateShinkansenType = this.reservingTrip.travelers.length === 1;
    if (this.reservingTrip.isShinkansenInfoRequired) {
      this.seat = this.setInitialTargetSeat();
      this.ticket = 'normal';
      this.preferredTicketingMethod =
        args.preferredTicketingMethod ??
        (this.user.organization?.usePostal && !this.user.organization?.useQr
          ? 'postal'
          : this.user.organization?.useQr || this.user.organization?.usePostal
          ? 'qr'
          : null);
      this.reservingTrip.travelers.forEach(traveler => {
        if (this.isSeparateShinkansenType) {
          traveler.shinkansenSeatType = this.isAvailableShinkansenSeatType(traveler.shinkansenSeatType)
            ? traveler.shinkansenSeatType
            : this.seat;
          traveler.shinkansenTicketType = traveler.shinkansenTicketType || this.ticket;
        } else {
          traveler.shinkansenSeatType = this.seat;
          traveler.shinkansenTicketType = this.ticket;
        }
        traveler.shinkansenSeat = traveler.shinkansenSeat || '指定なし';
      });
    } else {
      this.preferredTicketingMethod = null;
    }
    this.domesticExist = args.domesticExist;
    this.foreignExist = args.foreignExist;
    this.approveItems = [];
    this.approveItemsPath = '/organization/approve_items/display';
    this.paymentGatewayType = args.paymentGatewayType;
    this.cardNum01 = '';
    this.cardNum02 = '';
    this.cardNum03 = '';
    this.cardNum04 = '';
    this.cardExpireYear = '';
    this.cardExpireMonth = '';
    this.cardCode = '';
    this.cardName = '';
    this.rentalCarLimitType = args.rentalCarLimitType;
    this.workflowStyle = args.workflowStyle;
    this.useDefaultWorkflow = args.useDefaultWorkflow || false;
    this.expensesAccountTypeAvailable = args.expensesAccountTypeAvailable || false;
    this.paymentGatewayMembers = args.paymentGatewayMembers;
    this.paymentTransaction = args.paymentTransaction;
    this.approveItemValues = new Map<number, string>();
    this.approveItemValueCodes = new Map<number, string>();
    this.approveItemFiles = new Map<number, ImageFile>();
    this.approveItemFilesBase64 = new Map<number, string>();
    this.useChargingDepartmentWorkflow = args.useChargingDepartmentWorkflow ?? false;
    this.approvers = args.approvers;
    this.concurExpenseEnabled = args.concurExpenseEnabled ?? false;
    this.concurConnected = args.concurConnected ?? false;
    this.tabiregiConsent = args.tabiregiConsent ?? { consented: false, registeredAt: null };
    this.bakurakuWorkflowIntegrationEnabled = args.bakurakuWorkflowIntegrationEnabled ?? false;
    this.bakurakuWorkflowRequirementMode = args.bakurakuWorkflowRequirementMode ?? null;
    this.bakurakuWorkflowRequest = args.bakurakuWorkflowRequest ?? null;
    this.hasMembers = args.hasMembers;
    this.isSimpleRequest = args.isSimpleRequest || false;
    this.loginUserId = args.loginUserId;
    this.nationalities = args.nationalities;
    this.rentalCarAvailable = args.rentalCarAvailable;
    this.departmentDetailMode = 'item';
    this.projectDetailMode = 'item';
    this.expensesAccountTypeDetailMode = 'item';
    this.defaultWorkflowApprovers = args.defaultWorkflowApprovers;
    this.departments = args.departments || [];
    this.chargingDepartmentShares = args.chargingDepartmentShares || [];
    this.expensesAccountTypes = args.expensesAccountTypes || [];
    this.projectShareAvailability = args.projectShareAvailability;
    this.projects = args.projects || [];
    this.projectShares = args.projectShares || [];
    this.packageProvider = args.packageProvider;
    this.consignmentSale = args.consignmentSale;
    this.termsAgree = false;
    this.tripDuplications = args.tripDuplications || [];
    this.tripDuplicationAgree = false;
    this.restrictedHotelAgree = false;
    this.passportWarningTravelers = [];
    this.passportWarningAgree = false;
    this.showValidationErrors = false;

    if (this.reservingTrip.rentalCars.length > 0) {
      const [firstTraveler] = this.reservingTrip.travelers;
      this.reservingTrip.rentalCars.forEach(rentalCar => {
        rentalCar.carTypeOptions = rentalCar.carTypeOptions?.length
          ? rentalCar.carTypeOptions
          : this.reservingTrip.carTypeOptions;
        rentalCar.firstNameKana = rentalCar.firstNameKana || firstTraveler?.firstNameKana || '';
        rentalCar.lastNameKana = rentalCar.lastNameKana || firstTraveler?.lastNameKana || '';
        rentalCar.driverTel = resolveRentalCarDriverTel({
          driverTel: rentalCar.driverTel,
          traveler: firstTraveler
        });
      });
    }

    const organization = this.user.organization;
    this.paymentMethodType = organization?.paymentMethodType;
    this.paymentMethod = this.paymentMethodType !== 'card' ? 'billing' : 'card';
    if (
      this.paymentMethodType === 'card' &&
      this.paymentGatewayType === 'sony' &&
      !this.domesticExist &&
      this.foreignExist
    ) {
      this.paymentMethod = 'billing';
      this.paymentMethodType = 'invoice';
    }
    if (!this.paymentTransaction && this.paymentMethod === 'card' && this.paymentGatewayMembers?.length) {
      this.paymentTransaction = { uid: this.paymentGatewayMembers[0].uid };
    }
  }

  @computed get organization() {
    return this.user.organization;
  }

  private buildHotelsFromReservingTrip(): HotelInfo[] {
    const hotels = this.reservingTrip.items
      .map(item => item?.element)
      .filter((element): element is Hotel => element instanceof Hotel);

    if (hotels.length === 0) {
      return [];
    }

    return hotels.map(hotel => {
      const roomNum = Math.max(
        1,
        this.reservingTrip.isPackage && this.roomNum ? this.roomNum : hotel.roomnum || this.roomNum || 1
      );
      const normalizedBedTypes = (hotel.bedTypes ?? []).filter(
        (bedType): bedType is { id: number; description: string } =>
          typeof bedType.id === 'number' && typeof bedType.description === 'string'
      );
      const rooms = _.times(roomNum, roomIndex => {
        const traveler = this.reservingTrip.travelers[roomIndex] || this.reservingTrip.travelers[0];
        const travelerUser =
          traveler?.userId === this.user.id || traveler?.user?.id === this.user.id ? this.user : traveler?.user;
        const fallbackUser = travelerUser || this.user;
        const baseTraveler = travelerUser || traveler;
        const baseTravelerTel = travelerUser?.tel || traveler?.flightTel || '';
        const firstBedType = normalizedBedTypes[0];
        const birthday = traveler?.flightBirthday || fallbackUser?.birthday;
        const selectedStayPlan = hotel.stayPlans?.find(plan => plan.id === hotel.selectedStayPlanId);
        const bedTypeDescription =
          firstBedType?.description ||
          selectedStayPlan?.roomName?.replace(/^(禁煙|喫煙)/, '') ||
          hotel.roomType ||
          undefined;

        return {
          hotelFirstName: baseTraveler?.firstNameRoman || fallbackUser?.firstNameRoman || '',
          hotelLastName: baseTraveler?.lastNameRoman || fallbackUser?.lastNameRoman || '',
          hotelFirstNameKana: baseTraveler?.firstNameKana || fallbackUser?.firstNameKana || '',
          hotelLastNameKana: baseTraveler?.lastNameKana || fallbackUser?.lastNameKana || '',
          hotelBirthday: birthday ? moment(birthday).format('YYYY-MM-DD') : '',
          hotelGender: traveler?.flightGender || fallbackUser?.gender || '',
          hotelTel: baseTravelerTel || fallbackUser?.tel || '',
          bedTypes: normalizedBedTypes,
          bedTypeId: firstBedType?.id,
          bedTypeDescription
        };
      });

      return {
        id: hotel.id,
        name: hotel.name,
        type: hotel.type || '',
        rooms,
        plan_name: hotel.planName
      };
    });
  }

  @action syncHotelsFromTravelers() {
    const syncedHotels = this.buildHotelsFromReservingTrip();
    if (this.hotels.length === 0) {
      this.hotels = syncedHotels;
      return;
    }

    this.hotels = syncedHotels.map((syncedHotel, hotelIndex) => {
      const currentHotel = this.hotels[hotelIndex];
      if (!currentHotel?.rooms || !syncedHotel.rooms) {
        return currentHotel ? { ...currentHotel, ...syncedHotel } : syncedHotel;
      }

      return {
        ...currentHotel,
        ...syncedHotel,
        rooms: syncedHotel.rooms.map((syncedRoom, roomIndex) => {
          const currentRoom = currentHotel.rooms?.[roomIndex];
          if (!currentRoom) {
            return syncedRoom;
          }

          return {
            ...currentRoom,
            hotelFirstName: syncedRoom.hotelFirstName,
            hotelLastName: syncedRoom.hotelLastName,
            hotelFirstNameKana: syncedRoom.hotelFirstNameKana,
            hotelLastNameKana: syncedRoom.hotelLastNameKana,
            hotelBirthday: syncedRoom.hotelBirthday,
            hotelGender: syncedRoom.hotelGender,
            hotelTel: syncedRoom.hotelTel
          };
        })
      };
    });
  }

  @computed get canPayment() {
    if (
      this.paymentMethod === 'card' &&
      this.paymentGatewayType === 'sony' &&
      this.domesticExist &&
      this.foreignExist
    ) {
      return false;
    }
    return true;
  }

  @computed get setting() {
    return this.user.organization?.setting;
  }

  @computed get applicant() {
    const applicants = this.reservingTrip.travelers.filter(t => t.user?.department?.approvers);
    let applicant = _.first(applicants);
    if (!applicant) {
      applicant = new TravelerInformation({});
      applicant.assignFromUser(this.user);
    }
    return applicant;
  }

  @computed get displayApproverList(): ApproverList | undefined {
    // 費用負担部署のワークフロー
    if (this.useChargingDepartmentWorkflow) {
      if (!this.chargingDepartment) {
        return undefined;
      }
      const approverData = this.chargingDepartment.approvalAuthorities?.map(aa => ({
        approve_stage: aa.approveStage,
        name: aa.approver?.name
      }));
      const approverList = new ApproverList(approverData ?? []);
      if (approverList.list.length === 0) {
        return undefined;
      }
      return approverList;
    }
    const applicantId = this.applicant.userId ?? this.applicant.user?.id ?? this.applicant.id;
    const loginUserId = this.loginUserId ?? this.user.id;
    // 申請者が本人ならapprovers、申請者が本人でない（代理手配）ならapplicant.user.department.approvers
    const approverSource =
      applicantId != null && loginUserId != null && String(applicantId) !== String(loginUserId)
        ? this.applicant.user?.department?.approvers
        : this.approvers;
    const approverList = new ApproverList(_.sortBy(approverSource ?? [], 'approve_stage'));
    if (approverList.list.length === 0) {
      return undefined;
    }
    return approverList;
  }

  @computed get isApprovalRequired() {
    const applicant = this.applicant;
    if (applicant) {
      if (this.foreignExist) {
        return applicant.user?.foreignApprovalRequired;
      }
      return applicant.user?.domesticApprovalRequired;
    }
    return undefined;
  }

  @computed get isRentalCarRequired() {
    return this.rentalCarRequired === 'true' ? true : undefined;
  }

  @computed get hotelsParam() {
    return _.map(this.hotels, hotel => ({
      id: hotel.id,
      rooms: _.map(hotel.rooms, room => ({
        guest_first_name: room.hotelFirstName,
        guest_last_name: room.hotelLastName,
        guest_first_name_kana: room.hotelFirstNameKana,
        guest_last_name_kana: room.hotelLastNameKana,
        guest_birthday: room.hotelBirthday,
        guest_gender: room.hotelGender,
        guest_tel: room.hotelTel,
        bed_type: room.bedTypeId,
        bed_type_description: room.bedTypeDescription,
        plan_name: hotel.plan_name
      }))
    }));
  }

  @computed get newCardParams() {
    return {
      cardno: this.cardNum01 + this.cardNum02 + this.cardNum03 + this.cardNum04,
      expire: `${this.cardExpireYear}${this.cardExpireMonth}`,
      expireYear: this.cardExpireYear,
      expireMonth: this.cardExpireMonth,
      securitycode: this.cardCode,
      holdername: this.cardName
    };
  }

  @computed get postcodeFormatted() {
    if (!this.postcode.includes('-') && this.postcode !== '') {
      return this.insertHyphen(this.postcode, 3, '-');
    }
    return this.postcode;
  }

  @computed get travelerGrades() {
    return this.reservingTrip.travelers
      .map(t => t.user?.grade)
      .filter((grade): grade is Grade => grade !== undefined);
  }

  @computed get availableShinkansenSeatTypes() {
    return this.travelerGrades.reduce<ShinkansenSeatType[]>(
      (seatTypes, grade) => seatTypes.concat(Array.from(grade.shinkansenSeatTypes)),
      []
    );
  }

  @computed get isShinkansenFreeSeatAvailable() {
    // 自由席が存在しない場合はfalseを返す
    if (!this.reservingTrip.isUnreservedSeatAvailable) {
      return false;
    }

    // ユーザーの役職がない場合はtrueを返す
    if (this.travelerGrades.length === 0) {
      return true;
    }
    if (this.availableShinkansenSeatTypes.includes('free')) {
      return true;
    }
    return false;
  }

  @computed get isShinkansenReserveSeatAvailable() {
    if (!this.reservingTrip.isUnreservedSeatAvailable) {
      return true;
    }

    // ユーザーの役職がない場合はtrueを返す
    if (this.travelerGrades.length === 0) {
      return true;
    }
    if (this.availableShinkansenSeatTypes.includes('reserve')) {
      return true;
    }
    return false;
  }

  @computed get isShinkansenGreenSeatAvailable() {
    if (this.travelerGrades.length === 0) {
      return true;
    }
    if (this.availableShinkansenSeatTypes.includes('green')) {
      return true;
    }
    return false;
  }

  isExOnlyShinkansenTicketing() {
    if (!this.reservingTrip.isShinkansenInfoRequired) {
      return false;
    }
    if (!this.isSeparateShinkansenType) {
      return this.ticket === 'ex';
    }
    if (this.reservingTrip.travelers.length === 0) {
      return false;
    }
    return this.reservingTrip.travelers.every(traveler => traveler.shinkansenTicketType === 'ex');
  }

  isShinkansenPostalTicketing() {
    if (!this.reservingTrip.isShinkansenInfoRequired || this.isExOnlyShinkansenTicketing()) {
      return false;
    }
    if (this.user.organization?.useQr && this.user.organization?.usePostal) {
      return this.preferredTicketingMethod === 'postal';
    }
    return !!this.user.organization?.usePostal && !this.user.organization?.useQr;
  }

  isShinkansenQrTicketing() {
    if (!this.reservingTrip.isShinkansenInfoRequired || this.isExOnlyShinkansenTicketing()) {
      return false;
    }
    if (this.user.organization?.useQr && this.user.organization?.usePostal) {
      return this.preferredTicketingMethod === 'qr';
    }
    return !!this.user.organization?.useQr && !this.user.organization?.usePostal;
  }

  setInitialTargetSeat(): ShinkansenSeatType | undefined {
    if (this.availableShinkansenSeatTypes.length > 0) {
      if (this.availableShinkansenSeatTypes.includes('reserve')) {
        return 'reserve';
      }
      if (this.availableShinkansenSeatTypes.includes('free')) {
        return this.reservingTrip.isUnreservedSeatAvailable ? 'free' : 'reserve';
      }
      return 'green';
    }

    if (this.reservingTrip.isUnreservedSeatAvailable) {
      return 'free';
    }

    return undefined;
  }

  isAvailableShinkansenSeatType(seatType: ShinkansenSeatType | undefined) {
    switch (seatType) {
      case 'free':
        return this.isShinkansenFreeSeatAvailable;
      case 'reserve':
        return this.isShinkansenReserveSeatAvailable;
      case 'green':
        return this.isShinkansenGreenSeatAvailable;
      default:
        return false;
    }
  }

  @action syncSharedShinkansenSeatFromTravelerGrades() {
    if (!this.reservingTrip.isShinkansenInfoRequired) {
      return;
    }

    const seat = this.setInitialTargetSeat();
    if (!seat) {
      return;
    }

    this.seat = seat;
    this.reservingTrip.travelers.forEach(traveler => {
      if (this.isSeparateShinkansenType && this.isAvailableShinkansenSeatType(traveler.shinkansenSeatType)) {
        return;
      }
      traveler.shinkansenSeatType = seat;
      traveler.shinkansenSeat = traveler.shinkansenSeat || '指定なし';
    });
  }

  @action initOrderItemMappings(target: OrderItemMappingListTarget) {
    if (target === 'department') {
      if (this.departmentDetailMode === 'item') {
        this.orderItemMappingsDepartmentPart = this.initialListForItem;
      } else if (this.departmentDetailMode === 'traveler') {
        this.orderItemMappingsDepartmentPart = this.initialListForTraveler;
      } else if (this.departmentDetailMode === 'both') {
        this.orderItemMappingsDepartmentPart = this.initialListForBoth;
      }
    } else if (target === 'project') {
      if (this.projectDetailMode === 'item') {
        this.orderItemMappingsProjectPart = this.initialListForItem;
      } else if (this.projectDetailMode === 'traveler') {
        this.orderItemMappingsProjectPart = this.initialListForTraveler;
      } else if (this.projectDetailMode === 'both') {
        this.orderItemMappingsProjectPart = this.initialListForBoth;
      }
    } else if (target === 'expenses_account_type') {
      if (this.expensesAccountTypeDetailMode === 'item') {
        this.orderItemMappingsExpensesPart = this.initialListForItem;
      } else if (this.expensesAccountTypeDetailMode === 'traveler') {
        this.orderItemMappingsExpensesPart = this.initialListForTraveler;
      } else if (this.expensesAccountTypeDetailMode === 'both') {
        this.orderItemMappingsExpensesPart = this.initialListForBoth;
      }
    }
  }

  @computed get initialListForItem(): OrderItemMapping[] {
    const orderItemMappings: OrderItemMapping[] = [];
    if (this.reservingTrip.isPackage) {
      orderItemMappings.push(
        new OrderItemMapping({
          travelerId: -1,
          orderItemType: 'package',
          orderItemIndex: 1
        })
      );
    } else {
      const list =
        this.reservingTrip.items && this.reservingTrip.items.length > 0
          ? this.reservingTrip.items
          : this.reservingTrip.nonOrderItems;
      list.forEach(item => {
        orderItemMappings.push(
          new OrderItemMapping({
            travelerId: -1,
            orderItemType: 'item',
            orderItemIndex: Number(item?.index)
          })
        );
      });
    }

    [...Array(this.reservingTrip.rentalCars.length)].forEach((_r, i) => {
      orderItemMappings.push(
        new OrderItemMapping({
          travelerId: -1,
          orderItemType: 'rental_car',
          orderItemIndex: i
        })
      );
    });

    return orderItemMappings;
  }

  @computed get initialListForTraveler(): OrderItemMapping[] {
    const orderItemMappings: OrderItemMapping[] = [];
    this.reservingTrip.travelers.forEach((_t, index) => {
      orderItemMappings.push(
        new OrderItemMapping({
          travelerId: index,
          orderItemType: 'all',
          orderItemIndex: -1
        })
      );
    });

    return orderItemMappings;
  }

  @computed get initialListForBoth(): OrderItemMapping[] {
    const orderItemMappings: OrderItemMapping[] = [];
    if (this.reservingTrip.isPackage) {
      this.reservingTrip.travelers.forEach((_t, index) => {
        orderItemMappings.push(
          new OrderItemMapping({
            travelerId: index,
            orderItemType: 'package',
            orderItemIndex: 1
          })
        );
      });
    } else {
      const list =
        this.reservingTrip.items && this.reservingTrip.items.length > 0
          ? this.reservingTrip.items
          : this.reservingTrip.nonOrderItems;
      list.forEach(item => {
        this.reservingTrip.travelers.forEach((_t, index) => {
          orderItemMappings.push(
            new OrderItemMapping({
              travelerId: index,
              orderItemType: 'item',
              orderItemIndex: Number(item?.index)
            })
          );
        });
      });
    }

    this.reservingTrip.rentalCars.forEach((_r, i) => {
      this.reservingTrip.travelers.forEach((_t, index) => {
        orderItemMappings.push(
          new OrderItemMapping({
            travelerId: index,
            orderItemType: 'rental_car',
            orderItemIndex: i
          })
        );
      });
    });

    return orderItemMappings;
  }

  private insertHyphen(postcode: string, index: 3, hyphen: '-') {
    this.postcode = postcode.slice(0, index) + hyphen + postcode.slice(index, postcode.length);
    return this.postcode;
  }

  @computed get fixOrderItemMappings() {
    const projectPart = this.separateOrderItemMappings(
      Array.from(this.orderItemMappingsProjectPart).map(m => m.submitParams)
    );
    const departmentPart = this.separateOrderItemMappings(
      Array.from(this.orderItemMappingsDepartmentPart).map(m => m.submitParams)
    );
    const expensesPart = this.separateOrderItemMappings(
      Array.from(this.orderItemMappingsExpensesPart).map(m => m.submitParams)
    );
    return ReserveInfo.mergeOrderItemMappings(projectPart, departmentPart, expensesPart);
  }

  @computed get fixNonOrderItemMappings() {
    const projectPart = this.separateNonOrderItemMappings(
      Array.from(this.orderItemMappingsProjectPart).map(m => m.submitParams)
    );
    const departmentPart = this.separateNonOrderItemMappings(
      Array.from(this.orderItemMappingsDepartmentPart).map(m => m.submitParams)
    );
    const expensesPart = this.separateNonOrderItemMappings(
      Array.from(this.orderItemMappingsExpensesPart).map(m => m.submitParams)
    );
    return ReserveInfo.mergeOrderItemMappings(projectPart, departmentPart, expensesPart);
  }

  @computed get itemList(): ReservingTripItem[] | NonOrderItem[] {
    const { items, nonOrderItems } = this.reservingTrip || {};
    return items && items.length > 0 ? items : nonOrderItems;
  }

  private separateOrderItemMappings(originalMappings: Record<string, unknown>[]): Record<string, unknown>[] {
    const travelerSeparated: Record<string, unknown>[] = [];
    originalMappings.forEach(mapping => {
      const mappingObj = mapping as {
        travelerId?: number;
        traveler_id?: number;
        orderItemType?: string;
        order_item_type?: string;
        orderItemIndex?: number;
        order_item_index?: number;
        projectId?: number;
        project_id?: number;
        projectShareId?: number;
        project_share_id?: number;
        chargingDepartmentId?: number;
        charging_department_id?: number;
        chargingDepartmentShareId?: number;
        charging_department_share_id?: number;
        expensesAccountTypeId?: number;
        expenses_account_type_id?: number;
        submitParams?: () => Record<string, unknown>;
      };
      const travelerId = mappingObj.travelerId ?? mappingObj.traveler_id ?? -1;
      if (travelerId === -1) {
        this.reservingTrip.travelers.forEach((traveler, traveler_index) => {
          travelerSeparated.push({
            traveler_id: traveler_index,
            order_item_type: mappingObj.orderItemType ?? mappingObj.order_item_type,
            order_item_index: mappingObj.orderItemIndex ?? mappingObj.order_item_index,
            project_id: mappingObj.projectId ?? mappingObj.project_id ?? undefined,
            project_share_id: mappingObj.projectShareId ?? mappingObj.project_share_id ?? undefined,
            charging_department_id:
              mappingObj.chargingDepartmentId ?? mappingObj.charging_department_id ?? undefined,
            charging_department_share_id:
              mappingObj.chargingDepartmentShareId ?? mappingObj.charging_department_share_id ?? undefined,
            expenses_account_type_id:
              mappingObj.expensesAccountTypeId ?? mappingObj.expenses_account_type_id ?? undefined
          });
        });
      } else {
        travelerSeparated.push(mapping);
      }
    });

    const results: Record<string, unknown>[] = [];
    travelerSeparated.forEach(mapping => {
      if (mapping.order_item_type === 'all') {
        if (this.reservingTrip.isPackage) {
          results.push({
            traveler_id: mapping.traveler_id,
            order_item_type: 'package',
            order_item_index: 1,
            project_id: mapping.project_id || undefined,
            project_share_id: mapping.project_share_id || undefined,
            charging_department_id: mapping.charging_department_id || undefined,
            charging_department_share_id: mapping.charging_department_share_id || undefined,
            expenses_account_type_id: mapping.expenses_account_type_id || undefined
          });
        } else {
          this.reservingTrip.items.forEach((item, i) => {
            results.push({
              traveler_id: mapping.traveler_id,
              order_item_type: 'item',
              order_item_index: i,
              project_id: mapping.project_id || undefined,
              project_share_id: mapping.project_share_id || undefined,
              charging_department_id: mapping.charging_department_id || undefined,
              charging_department_share_id: mapping.charging_department_share_id || undefined,
              expenses_account_type_id: mapping.expenses_account_type_id || undefined
            });
          });
        }

        if (this.isRentalCarRequired) {
          this.reservingTrip.rentalCars.forEach((car, i) => {
            results.push({
              traveler_id: mapping.traveler_id,
              order_item_type: 'rental_car',
              order_item_index: i,
              project_id: mapping.project_id || undefined,
              project_share_id: mapping.project_share_id || undefined,
              charging_department_id: mapping.charging_department_id || undefined,
              charging_department_share_id: mapping.charging_department_share_id || undefined,
              expenses_account_type_id: mapping.expenses_account_type_id || undefined
            });
          });
        }
      } else {
        results.push(mapping);
      }
    });
    return results;
  }

  private separateNonOrderItemMappings(originalMappings: Record<string, unknown>[]): Record<string, unknown>[] {
    const travelerSeparated: Record<string, unknown>[] = [];
    originalMappings.forEach(mapping => {
      const mappingObj = mapping as {
        travelerId?: number;
        traveler_id?: number;
        orderItemType?: string;
        order_item_type?: string;
        orderItemIndex?: number;
        order_item_index?: number;
        projectId?: number;
        project_id?: number;
        projectShareId?: number;
        project_share_id?: number;
        chargingDepartmentId?: number;
        charging_department_id?: number;
        chargingDepartmentShareId?: number;
        charging_department_share_id?: number;
        expensesAccountTypeId?: number;
        expenses_account_type_id?: number;
        submitParams?: () => Record<string, unknown>;
      };
      const travelerId = mappingObj.travelerId ?? mappingObj.traveler_id ?? -1;
      if (travelerId === -1) {
        this.reservingTrip.travelers.forEach((traveler, traveler_index) => {
          travelerSeparated.push({
            traveler_id: traveler_index,
            order_item_type: mappingObj.orderItemType ?? mappingObj.order_item_type,
            order_item_index: mappingObj.orderItemIndex ?? mappingObj.order_item_index,
            project_id: mappingObj.projectId ?? mappingObj.project_id ?? undefined,
            project_share_id: mappingObj.projectShareId ?? mappingObj.project_share_id ?? undefined,
            charging_department_id:
              mappingObj.chargingDepartmentId ?? mappingObj.charging_department_id ?? undefined,
            charging_department_share_id:
              mappingObj.chargingDepartmentShareId ?? mappingObj.charging_department_share_id ?? undefined,
            expenses_account_type_id:
              mappingObj.expensesAccountTypeId ?? mappingObj.expenses_account_type_id ?? undefined
          });
        });
      } else {
        travelerSeparated.push(mapping);
      }
    });

    const results: Record<string, unknown>[] = [];
    travelerSeparated.forEach(mapping => {
      if (mapping.order_item_type === 'all') {
        this.reservingTrip?.nonOrderItems.forEach((item, i) => {
          results.push({
            traveler_id: mapping.traveler_id,
            detail_type: mapping.order_item_type,
            order_item_type: 'item',
            order_item_index: i,
            project_id: mapping.project_id || undefined,
            project_share_id: mapping.project_share_id || undefined,
            charging_department_id: mapping.charging_department_id || undefined,
            charging_department_share_id: mapping.charging_department_share_id || undefined,
            expenses_account_type_id: mapping.expenses_account_type_id || undefined
          });
        });
      } else {
        results.push({
          ...mapping,
          detail_type: 'detail'
        });
      }
    });
    return results;
  }

  private static mergeOrderItemMappings(
    projectMappings: Record<string, unknown>[],
    departmentMappings: Record<string, unknown>[],
    expensesMappings: Record<string, unknown>[]
  ): Record<string, unknown>[] {
    const merged: Record<string, unknown>[] = projectMappings.map(projectMapping => {
      const departmentMapping = departmentMappings.find(
        department =>
          department.traveler_id === projectMapping.traveler_id &&
          department.order_item_type === projectMapping.order_item_type &&
          department.order_item_index === projectMapping.order_item_index
      );
      const expensesMapping = expensesMappings.find(
        expenses =>
          expenses.traveler_id === projectMapping.traveler_id &&
          expenses.order_item_type === projectMapping.order_item_type &&
          expenses.order_item_index === projectMapping.order_item_index
      );
      projectMapping.charging_department_id = departmentMapping?.charging_department_id;
      projectMapping.charging_department_share_id = departmentMapping?.charging_department_share_id;
      projectMapping.expenses_account_type_id = expensesMapping?.expenses_account_type_id;
      return projectMapping;
    });
    return merged;
  }

  @computed get approveItemParams() {
    const data = this.approveItems.map(r => ({
      id: r.id,
      userDisplayName: r.userDisplayName,
      dataType: r.dataType,
      display: r.display,
      requiredType: r.requiredType,
      placeholder: r.placeholder,
      value: this.approveItemValues.get(r.id),
      valueCode: this.approveItemValueCodes.get(r.id),
      file: this.getApproveItemFileBase64(r.id),
      fileType: this.getApproveItemFileType(r.id)
    }));
    return JSON.stringify(data);
  }

  private getApproveItemFileBase64(id: number) {
    return this.approveItemFilesBase64.get(id);
  }

  private getApproveItemFileType(id: number) {
    const file = this.approveItemFiles.get(id);
    if (file) {
      return file.type;
    }
    return '';
  }

  setApproveItemValue(id: number, value: string) {
    this.approveItemValues.set(id, value);
  }

  setApproveItemValueCode(id: number, value: string) {
    this.approveItemValueCodes.set(id, value);
  }

  async setApproveItemFile(id: number, file: ImageFile) {
    this.approveItemFiles.set(id, file);
    this.approveItemValues.set(id, file.name);
    this.approveItemFilesBase64.set(id, await this.convertFileToBase64(file));
  }

  @action
  setBakurakuWorkflowRequest(value: BakurakuWorkflowRequest | null) {
    this.bakurakuWorkflowRequest = value;
  }

  private async convertFileToBase64(file: ImageFile) {
    if (file) {
      const result = await new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);
        fileReader.onload = () => {
          resolve(fileReader.result as string);
        };
        fileReader.onerror = error => {
          reject(error);
        };
      });
      return result as string;
    }
    return '';
  }

  async fetchApproveItems(target: string) {
    await Fetcher.get<ApproveItemsResponse>(`${this.approveItemsPath}.json`, {
      foreign_exist: this.foreignExist,
      target
    })
      .then(
        result => {
          this.approveItems = result.approve_items.map(
            item => new ApproveItem(convertApproveItemResponseToArgs(item))
          );
          if (result.approve_items.length > 0) {
            result.approve_items.forEach(item => {
              if (item.default_item_code != null && item.default_item_name != null) {
                this.setApproveItemValueCode(item.id, item.default_item_code);
                this.setApproveItemValue(item.id, item.default_item_name);
              }
            });
          }
        },
        _error => {
          throw _error;
        }
      )
      .catch(e => {
        throw e;
      });
  }

  async searchBakurakuWorkflowRequests(args: {
    travelerEmails: string[];
    keyword?: string;
    requestStatus?: string;
  }) {
    const result = await Fetcher.get<BakurakuWorkflowRequestsResponse>(
      '/reserve_confirm/bakuraku_workflow_requests',
      {
        traveler_emails: args.travelerEmails,
        keyword: args.keyword,
        status: args.requestStatus,
        request_date: Date.now()
      }
    );

    return result.bakuraku_workflow_requests.map(
      request => new BakurakuWorkflowRequest(convertBakurakuWorkflowRequestResponseToArgs(request))
    );
  }

  fetchAttachedApproveItemFiles(tripId: number, items: TripApproveItemJson[]) {
    items.forEach(item => {
      if (item.dataType === 'file' && item.filePath) {
        fetch(`/trips/${tripId}/approve_item_files/${item.id}`)
          .then(result => {
            return result.blob();
          })
          .then(blob => {
            const file = new File([blob], item.value, { type: item.fileType });
            this.setApproveItemFile(item.id, file);
          });
      }
    });
  }

  @computed get submitParams() {
    const params: Record<string, unknown> = {
      shinkansen_address:
        this.shinkansenAddressType === 'text'
          ? this.shinkansenAddress
          : this.shinkansenAddressType === 'organization_base_select'
          ? this.organizationBase && this.organizationBase.address
          : this.organizationAddress?.location ?? null,
      shinkansen_addressee:
        this.shinkansenAddressType === 'text'
          ? this.shinkansenAddressee
          : this.shinkansenAddressType === 'select'
          ? this.organizationAddress?.addressee ?? null
          : this.shinkansenAddressType === 'organization_base_select'
          ? this.organizationBase && this.organizationBase.addressee
          : '',
      shinkansen_address_type: this.shinkansenAddressType,
      postcode:
        this.shinkansenAddressType === 'text'
          ? this.postcodeFormatted
          : this.shinkansenAddressType === 'select'
          ? this.organizationAddress?.postalCode ?? null
          : this.shinkansenAddressType === 'organization_base_select'
          ? this.organizationBase && this.organizationBase.postalCode
          : '',
      organization_address_id: this.organizationAddress?.id ?? null,
      // MobXのobservable配列は送信時にHash化されることがあるため、配列化してからパラメータ化する
      travelers: Array.from(this.reservingTrip.travelers).map(traveler =>
        traveler.getTravelerParams(this.reservingTrip.isForeignFlightCustomerInfoRequired, {
          isShinkansenInfoRequired: this.reservingTrip.isShinkansenInfoRequired,
          isHotelInfoRequired: this.reservingTrip.isHotelInfoRequired
        })
      ),
      all_travelers_notification: this.allTravelersNotification,
      notified_user_ids: this.notifiedUsers.map(user => user?.id).filter(id => id !== undefined),
      internal_number: this.internalNumber,
      approval_required: this.isApprovalRequired,
      final_destination: this.finalDestination,
      purpose: this.purpose,
      hotels: this.hotelsParam,
      applicant_id: this.applicant.userId ?? this.applicant.user?.id ?? this.applicant.id,
      search_query_id: this.searchQueryId,
      charging_department_id: this.chargingDepartment && this.chargingDepartment.id,
      project_id: this.projectId != null ? String(this.projectId) : '',
      expenses_account_type_id: this.expensesAccountTypeId != null ? String(this.expensesAccountTypeId) : '',
      order_item_mappings: this.fixOrderItemMappings,
      non_order_item_mappings: this.fixNonOrderItemMappings,
      show_department_detail:
        this.showDepartmentDetail ||
        this.orderItemMappingsDepartmentPart.some(
          mapping =>
            !(mapping.travelerId === -1 && mapping.orderItemType === 'all' && mapping.orderItemIndex === -1)
        ),
      show_project_detail:
        this.showProjectDetail ||
        this.orderItemMappingsProjectPart.some(
          mapping =>
            !(mapping.travelerId === -1 && mapping.orderItemType === 'all' && mapping.orderItemIndex === -1)
        ),
      show_expenses_account_type_detail:
        this.showExpensesAccountTypeDetail ||
        this.orderItemMappingsExpensesPart.some(
          mapping =>
            !(mapping.travelerId === -1 && mapping.orderItemType === 'all' && mapping.orderItemIndex === -1)
        ),
      type: this.type,
      package_type: this.packageType,
      package_search_condition: { select_page_url: this.packageSearchCondition?.selectPageUrl },
      rental_car_required: this.isRentalCarRequired,
      rental_cars: [],
      private_use: this.privateUse,
      approve_stages: JSON.stringify(buildApproveStagesPayload(this.approveStages)),
      user_checker: this.payloadUserChecker,
      preferred_ticketing_method: this.preferredTicketingMethod,
      bakuraku_workflow_request_id: this.bakurakuWorkflowRequest?.bakurakuWorkflowRequestId || '',
      bakuraku_workflow_request_title: this.bakurakuWorkflowRequest?.bakurakuWorkflowRequestTitle || '',
      bakuraku_workflow_request_num: this.bakurakuWorkflowRequest?.bakurakuWorkflowRequestNum || '',
      approve_items: this.approveItemParams,
      passport_warning_travelers: this.passportWarningTravelers.map(traveler => ({
        user_id: traveler.userId,
        passport_expire: traveler.passportExpire
      }))
    };
    if (this.hotels && this.hotels.length > 0) {
      const hotel = this.hotels[0];
      if (hotel.rooms && hotel.rooms.length > 0) {
        const room = hotel.rooms[0];
        params.last_name = room.hotelLastName;
        params.first_name = room.hotelFirstName;
        params.guest_last_name = room.hotelLastName;
        params.guest_first_name = room.hotelFirstName;
        params.guest_last_name_kana = room.hotelLastNameKana;
        params.guest_first_name_kana = room.hotelFirstNameKana;
        params.tel = room.hotelTel;
      }
    }
    if (this.seat && this.ticket !== 'bulk_ticket' && !this.isSeparateShinkansenType) {
      params.sheet = this.seat;
    }
    if (this.ticket && !this.isSeparateShinkansenType) {
      params.ticket = this.ticket;
    }
    if (this.reservingTrip.items) {
      params.items = _.map(Array.from(this.reservingTrip.items), item => item && item.submitParams);
    }
    if (this.rentalCarRequired === 'true') {
      params.rental_cars = Array.from(this.reservingTrip.rentalCars).map(rentalCar => rentalCar.submitParams);
    }
    return params;
  }

  private static requiredError(name: string, value: string | undefined | null, message = 'を入力してください。') {
    if (!value || value.length === 0) {
      return name + message;
    }
    return undefined;
  }

  private static flightBirthdayRequiredError(name: string, value: string | undefined | null) {
    if (!value || value.length === 0) {
      return '搭乗者の誕生日が設定されておりません。 該当ユーザーでログイン後、アカウント設定画面より誕生日を設定していただくか、管理者が社員マスタより設定してください。';
    }
    return undefined;
  }

  public static telNumberError(name: string, value: string) {
    if (!value || value.trim() === '') {
      return `${name}を入力してください。`;
    }
    const trimmedValue = value.trim();
    if (!trimmedValue.match(/^[0-9-]+$/)) {
      return `${name}は数字とハイフンのみで入力してください。`;
    }
    const digitsOnly = trimmedValue.replace(/-/g, '');
    if (digitsOnly.length < 8) {
      return `${name}は短すぎます。最低8桁の数字が必要です。`;
    }
    if (digitsOnly.length > 15) {
      return `${name}は長すぎます。最大15桁までの数字にしてください。`;
    }
    return undefined;
  }

  private static surrogatePairError(name: string, value: string) {
    const surrogatePairs = value.match(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g) || [];
    if (surrogatePairs.length > 0) {
      return `${name}に環境依存文字「${surrogatePairs.join('')}」が含まれています`;
    }
    return undefined;
  }

  private static postcodeError(name: string, value: string) {
    if (!value.match(/^\d{3}-?\d{4}$/)) {
      return `${name}を正しく入力してください。`;
    }
    return undefined;
  }

  private static phoneError(name: string, value: string) {
    const phoneNumber = value.replace(/-/g, '');
    if (phoneNumber.length > 11) {
      return `${name}は番号11桁以下で入力して下さい。`;
    }
    return undefined;
  }

  private static katakanaError(name: string, value: string) {
    if (!value.match(/^[ァ-ヶー　]*$/)) {
      return `${name}はカタカナで入力してください。`;
    }
    return undefined;
  }

  private static alphabetError(name: string, value: string) {
    const error = ReserveInfo.requiredError(name, value);
    if (error) {
      return error;
    }
    if (!value.match(/^[a-zA-Z]+$/)) {
      return `${name}はアルファベッドで入力してください。`;
    }
    return undefined;
  }

  private static alphabetErrorAllowEmpty(name: string, value: string) {
    if (!value || value.length === 0) {
      return undefined;
    }
    if (!value.match(/^[a-zA-Z]+$/)) {
      return `${name}はアルファベッドで入力してください。`;
    }
    return undefined;
  }

  private static kanaError(name: string, value = '') {
    const error = ReserveInfo.requiredError(name, value);
    if (error) {
      return error;
    }
    if (!value.match(/^[ァ-ヶｦ-ﾟー]+$/)) {
      return `${name}はカタカナで入力してください。`;
    }
    return undefined;
  }

  private static flightBirthdayError(name: string, value: string) {
    const error = ReserveInfo.flightBirthdayRequiredError(name, value);
    if (error) {
      return error;
    }
    if (!moment(value).isValid()) {
      return `${name}を正しく入力してください。（例：1986-01-01）`;
    }
    return undefined;
  }

  @computed get organizationAddressError() {
    const value = this.organizationAddress && this.organizationAddress.location;
    if (!value || value.length === 0) {
      return '選択された部署に住所が登録されていません。別の部署を選択するか、手動で入力してください。';
    }
    return undefined;
  }

  private static expiredError(name: string, value: string) {
    const error = ReserveInfo.requiredError(name, value);
    if (error) {
      return error;
    }
    if (isOverDate(value)) {
      return '有効期限が切れています。パスポートをご確認ください。内容が変更されている場合、アカウント設定から変更してください。';
    }
    return undefined;
  }

  private static requireProjectMappingError(name: string, values: Record<string, unknown>[]) {
    const result = values.find(v => _.isNil(v.project_id) && _.isNil(v.project_share_id));
    if (result) {
      return `${name}を選択して下さい`;
    }
    return undefined;
  }

  private static rquireDepartmentMappingError(name: string, values: Record<string, unknown>[]) {
    const result = values.find(v => _.isNil(v.charging_department_id) && _.isNil(v.charging_department_share_id));
    if (result) {
      return `${name}を選択して下さい`;
    }
    return undefined;
  }

  private static requireExpensesMappingError(name: string, values: Record<string, unknown>[]) {
    const result = values.find(v => _.isNil(v.expenses_account_type_id));
    if (result) {
      return `${name}を選択して下さい`;
    }
    return undefined;
  }

  limitError(name: string, value: string | undefined) {
    if (!value) {
      return ReserveInfo.requiredError(name, value);
    }
    if (!this.rentalCarLimitType) {
      return undefined;
    }
    const actualDays = this.rentalCarLimitType.actualDays;
    if (
      isOverLimitDateHour(
        actualDays,
        this.rentalCarLimitType.hour,
        value,
        this.rentalCarLimitType.bookingTooLate,
        this.rentalCarLimitType.todayHoliday
      )
    ) {
      return `レンタカーの手配は${this.rentalCarLimitType.description()}が依頼期限です`;
    }
    return undefined;
  }

  @computed get isPurposeBusinessTripRequired(): boolean {
    return !_.isNil(this.setting) && this.setting.isPurposeBusinessTripRequired;
  }

  @computed get isDestinationBusinessTripRequired(): boolean {
    return !_.isNil(this.setting) && this.setting.isDestinationBusinessTripRequired;
  }

  @computed get isInternalNumberRequired(): boolean {
    return !_.isNil(this.setting) && this.setting.isInternalNumberRequired;
  }

  @computed get isBakurakuWorkflowRequired(): boolean {
    return this.bakurakuWorkflowIntegrationEnabled && this.bakurakuWorkflowRequirementMode === 'required';
  }

  @computed get showBakurakuWorkflowSection(): boolean {
    return this.bakurakuWorkflowIntegrationEnabled || this.bakurakuWorkflowRequest !== null;
  }

  @computed get isProjectNameRequired(): boolean {
    return !_.isNil(this.setting) && this.setting.isProjectNameRequired;
  }

  @computed get isChargingDepartmentRequired(): boolean {
    return !_.isNil(this.setting) && this.setting.isChargingDepartmentRequired;
  }

  @computed get isExpensesAccountTypeRequired(): boolean {
    return !_.isNil(this.setting) && this.setting.isExpensesAccountTypeRequired;
  }

  @computed get isEnabledAutoReserveRakutenHotel(): boolean {
    return this.user.organization?.enableAutoReserveRakutenHotel || false;
  }

  @computed get isInternalNumberShown(): boolean {
    const { setting } = this;
    if (!setting) return true;
    return setting.showInternalNumber;
  }

  @computed get isProjectNameShown(): boolean {
    const { setting } = this;
    if (!setting) return true;
    return setting.showProjectName;
  }

  @computed get isChargingDepartmentShown(): boolean {
    const { setting } = this;
    if (!setting) return true;
    return setting.showChargingDepartment;
  }

  @computed get isExpensesAcountTypeShown(): boolean {
    const { setting } = this;
    if (!setting) return true;
    return setting.showExpensesAccountType;
  }

  @computed get isDestinationBusinessTripShown(): boolean {
    const { setting } = this;
    if (!setting) return true;
    return setting.showDestinationBusinessTrip;
  }

  @computed get isPurposeBusinessTripShown(): boolean {
    const { setting } = this;
    if (!setting) return true;
    return setting.showPurposeBusinessTrip;
  }

  @computed get cardError(): string | undefined {
    if (
      this.paymentMethodType === 'card' &&
      this.paymentGatewayType === 'sony' &&
      (!this.paymentGatewayMembers || this.paymentGatewayMembers.length === 0)
    ) {
      return '支払い用のクレジットカードが登録されていません。管理者の方にご確認ください。';
    }
    const errors = [];
    if (this.paymentMethodType === 'card' && this.paymentTransaction?.uid === 'new') {
      if (this.cardNum01 === '' || this.cardNum02 === '' || this.cardNum03 === '' || this.cardNum04 === '') {
        errors.push('カード番号を正しく入力してください。');
      }
      if (this.cardExpireMonth === '' || this.cardExpireYear === '') {
        errors.push('有効期限を正しく入力してください。');
      }
      if (this.cardName === '') {
        errors.push('名義人の名前を入力してください。');
      }
      if (this.cardCode === '') {
        errors.push('セキュリティコードを入力してください。');
      }
    }
    if (errors.length > 0) {
      return errors.join('\n');
    }
    return undefined;
  }

  @computed get organizationErrors(): string | undefined {
    if (
      this.isApprovalRequired &&
      this.useDefaultWorkflow &&
      this.organizationError &&
      this.workflowStyle !== 'trip'
    ) {
      const errors: string[] = [];
      if (this.useChargingDepartmentWorkflow) {
        errors.push('費用負担部署またはその承認者が設定されていません。管理者に連絡してください。');
      } else {
        errors.push('出張者の所属部署またはその承認者が設定されていません。管理者に連絡してください。');
      }
      errors.push(`■${this.user.organization?.name || ''}の管理者`);
      this.user.organization?.admins?.forEach(admin => {
        errors.push(`${admin.name}: '${admin.email}`);
      });
      return errors.join('\n');
    }
    return undefined;
  }

  @computed get organizationError(): boolean | undefined {
    if (this.useChargingDepartmentWorkflow && this.chargingDepartment) {
      const approverList = this.displayApproverList;
      return !approverList || approverList.list.length === 0;
    }
    const applicant = this.applicant;
    if (applicant) {
      if (this.foreignExist) {
        return applicant.user?.foreignApprovalRequired === undefined;
      }
      return applicant.user?.domesticApprovalRequired === undefined;
    }
    return undefined;
  }

  @computed get firstExistAccountTraveler(): TravelerInformation | undefined {
    const accountTraveler = this.reservingTrip.travelers.find(t => t.user?.department);
    if (accountTraveler) {
      return accountTraveler;
    }
    const traveler = new TravelerInformation({});
    traveler.assignFromUser(this.user);
    return traveler;
  }

  @computed get departmentDetailOptions(): FilterableSelectorOption[] {
    const departmentsOption: FilterableSelectorOption[] = this.departments.map(d => ({
      label: d.descriptionWithCode,
      value: `department-${d.id?.toString()}`,
      matcher: d.descriptionWithCode
    }));
    const sharesOption: FilterableSelectorOption[] = this.chargingDepartmentShares.map(s => ({
      labelLines: s.codeAndNameLines,
      value: `share-${s.id.toString()}`,
      matcher: s.codeAndNameOneLine
    }));
    return departmentsOption.concat(sharesOption);
  }

  @computed get projectDetailOptions(): FilterableSelectorOption[] {
    const projectsOption: FilterableSelectorOption[] = this.projects.map(p => ({
      label: p.codeAndName,
      value: `project-${p.id?.toString()}`,
      matcher: p.codeAndName
    }));
    const sharesOption: FilterableSelectorOption[] = this.projectShares.map(s => ({
      labelLines: s.codeAndNameLines,
      value: `share-${s.id?.toString()}`,
      matcher: s.codeAndNameOneLine
    }));
    return projectsOption.concat(sharesOption);
  }

  isApproveItemRequiredWithWorkflow(requiredType: string) {
    if (requiredType !== 'required_with_workflow') {
      return false;
    }
    if (this.isApprovalRequired) {
      return true;
    }
    const departmentId = this.user.department?.id;
    if (departmentId === null || departmentId === undefined || String(departmentId) === '') {
      return true;
    }
    return false;
  }

  @computed get chargingDepartmentIDs(): number[] {
    return this.orderItemMappingsDepartmentPart
      .map(mapping => mapping.chargingDepartmentId)
      .filter(id => id !== undefined);
  }

  @computed get project(): Project | undefined {
    return this.projects.find(p => p.id === this.projectId);
  }

  @computed get projectOption(): FilterableSelectorOption | undefined {
    if (!this.project) {
      return undefined;
    }
    return {
      label: this.project.codeAndName,
      value: this.project.id?.toString() || '',
      matcher: this.project.codeAndName
    };
  }

  @computed get expensesAccountTypeOptions(): FilterableSelectorOption[] {
    const options = this.expensesAccountTypes.map(e => {
      let label = '';
      if (e.code !== null) {
        label += `${e.code}：`;
      }
      label += e.name;
      return {
        label,
        value: `expenses-${e.id?.toString()}`,
        matcher: label
      };
    });
    return options;
  }

  @computed get isPrivateUseShown(): boolean {
    return this.setting?.allowPrivateUse ?? false;
  }

  getNationalityName(nationalityId?: number): string {
    if (!nationalityId) {
      return '';
    }
    const nationality = this.nationalities.find(n => n.id === nationalityId);
    return nationality?.name || '';
  }

  @computed get seatText(): string | undefined {
    switch (this.seat) {
      case 'free':
        return '自由席';
      case 'reserve':
        return '指定席';
      case 'green':
        return 'グリーン車';
      default:
        return undefined;
    }
  }

  @computed get ticketText(): string | undefined {
    switch (this.ticket) {
      case 'normal':
        return '通常きっぷ';
      case 'kyuusyuu':
        return 'JR九州';
      case 'bulk_ticket':
        return '回数券';
      case 'ex':
        return 'EX-IC';
      default:
        return undefined;
    }
  }

  @computed get userChecker(): boolean {
    if (this.tripDuplications.length > 0 && !this.tripDuplicationAgree) {
      return false;
    }
    if (this.reservingTrip.restrictedHotels.length > 0 && !this.restrictedHotelAgree) {
      return false;
    }
    if (this.passportWarningTravelersForUi.length > 0 && !this.passportWarningAgree) {
      return false;
    }
    return true;
  }

  @computed get payloadUserChecker(): boolean {
    return this.restrictedHotelAgree;
  }

  bakurakuWorkflowRequestLabel() {
    return formatBakurakuWorkflowRequestLabel(this.bakurakuWorkflowRequest);
  }

  bakurakuTargetTravelerEmails() {
    return Array.from(
      new Set(
        this.reservingTrip.travelers
          .map(
            traveler =>
              traveler.user?.email || (traveler.userId === this.loginUserId ? this.user.email : undefined)
          )
          .filter(Boolean) as string[]
      )
    );
  }

  @computed get passportWarningTravelersForUi(): PassportWarningTravelerForUi[] {
    if (!this.reservingTrip.isForeignFlightCustomerInfoRequired) {
      return [];
    }
    return this.reservingTrip.travelers
      .filter(traveler => isWithinOneYear(traveler.passportExpire || undefined))
      .map(traveler => ({
        userId: traveler.userId ?? (typeof traveler.id === 'number' ? traveler.id : null),
        name: traveler.travelerName,
        passportExpire: traveler.passportExpire || '',
        remainingMonths: getMonthsUntilExpire(traveler.passportExpire || undefined) ?? 0
      }))
      .filter(traveler => traveler.passportExpire !== '');
  }

  @action setPassportWarningTravelers(travelers: PassportWarningTraveler[]) {
    this.passportWarningTravelers = travelers;
  }

  @action setShowValidationErrors(value: boolean) {
    this.showValidationErrors = value;
  }

  @computed get validationErrors() {
    if (!this.showValidationErrors) {
      return {};
    }
    const errors: { [key: string]: string | undefined } = {};
    const projectMapping = this.separateOrderItemMappings(
      this.orderItemMappingsProjectPart.map(m => m.submitParams)
    );
    const departmentMapping = this.separateOrderItemMappings(
      this.orderItemMappingsDepartmentPart.map(m => m.submitParams)
    );
    const expensesMapping = this.separateOrderItemMappings(
      this.orderItemMappingsExpensesPart.map(m => m.submitParams)
    );

    if (this.reservingTrip.items.some(item => item?.element instanceof Hotel)) {
      _.map(this.hotels, hotel => {
        _.map(hotel.rooms, (room, i) => {
          errors[`lastName_${i}`] = ReserveInfo.alphabetError('宿泊者名(氏)', room.hotelLastName);
          errors[`firstName_${i}`] = ReserveInfo.alphabetError('宿泊者名(名)', room.hotelFirstName);
          errors[`lastNameKana_${i}`] = ReserveInfo.kanaError('宿泊者名(氏カナ)', room.hotelLastNameKana);
          errors[`firstNameKana_${i}`] = ReserveInfo.kanaError('宿泊者名(名カナ)', room.hotelFirstNameKana);
          errors[`tel_${i}`] = ReserveInfo.requiredError('宿泊者電話番号', toDomesticTel(room.hotelTel));
          if (!errors[`tel_${i}`]) {
            errors[`tel_${i}`] = ReserveInfo.phoneError('宿泊者電話番号', toDomesticTel(room.hotelTel));
          }
        });
      });
    }

    if (this.isShinkansenPostalTicketing()) {
      if (this.shinkansenAddressType === 'text') {
        errors.postcode = ReserveInfo.postcodeError('郵便番号', this.postcode);
        if (_.isEmpty(errors.postcode)) {
          errors.postcode = ReserveInfo.requiredError('郵便番号', this.postcode);
        }
        errors.shinkansenAddress = ReserveInfo.requiredError('切符送り先住所', this.shinkansenAddress);
        errors.shinkansenAddressee = ReserveInfo.requiredError('切符送り先宛名', this.shinkansenAddressee);
      } else if (this.shinkansenAddressType === 'select') {
        errors.organizationAddress = this.organizationAddressError;
      }
    }

    if (this.isInternalNumberRequired) {
      errors.internalNumber = ReserveInfo.requiredError('社内管理番号', this.internalNumber);
    }
    if (this.isBakurakuWorkflowRequired) {
      errors.bakurakuWorkflowRequest = ReserveInfo.requiredError(
        'Bakuraku申請',
        this.bakurakuWorkflowRequestLabel()
      );
    }
    if (this.isProjectNameRequired) {
      if (this.showProjectDetail) {
        errors.projectId = ReserveInfo.requireProjectMappingError('プロジェクト名', projectMapping);
      } else {
        errors.projectId = ReserveInfo.requiredError('プロジェクト名', this.projectId?.toString());
      }
    }
    if (this.isChargingDepartmentRequired) {
      if (this.showDepartmentDetail) {
        errors.chargingDepartment = ReserveInfo.rquireDepartmentMappingError('費用負担部署', departmentMapping);
      } else {
        errors.chargingDepartment = ReserveInfo.requiredError(
          '費用負担部署',
          this.chargingDepartment && this.chargingDepartment.id?.toString()
        );
      }
    }
    Array.from(this.reservingTrip.travelers).forEach((t, i) => {
      if (this.user.organization) {
        switch (t.type) {
          case 'self':
            if (!t.id && !t.userId && !t.user?.id) {
              errors.memberSelect = '出張者を選択してください';
            }
            break;
          case 'member':
            if (!t.id && !t.userId && !t.user?.id) {
              errors.memberSelect = '出張者を選択してください';
            }
            break;
          case 'companion':
            if (this.reservingTrip.isForeignFlightCustomerInfoRequired) {
              errors.travelerFirstNameRomen = ReserveInfo.requiredError(
                '出張者の名前(ローマ字)',
                t.firstNameRoman
              );
              errors.travelerLastNameRomen = ReserveInfo.requiredError('出張者の名字(ローマ字)', t.lastNameRoman);
              errors.travelerFirstNameKana = ReserveInfo.requiredError(
                '出張者の名前(カナ)',
                t.firstNameKana || undefined
              );
              errors.travelerLastNameKana = ReserveInfo.requiredError(
                '出張者の名字(カナ)',
                t.lastNameKana || undefined
              );
              if (_.isEmpty(errors.travelerFirstNameRomen) && _.isEmpty(errors.travelerLastNameRomen)) {
                errors.travelerFirstNameRomen = ReserveInfo.alphabetError(
                  '出張者の名前(ローマ字)',
                  t.firstNameRoman || ''
                );
                errors.travelerLastNameRomen = ReserveInfo.alphabetError(
                  '出張者の名字(ローマ字)',
                  t.lastNameRoman || ''
                );
              }
              if (_.isEmpty(errors.travelerFirstNameKana) && _.isEmpty(errors.travelerLastNameKana)) {
                errors.travelerFirstNameKana = ReserveInfo.katakanaError(
                  '出張者の名前(カナ)',
                  t.firstNameKana || ''
                );
                errors.travelerLastNameKana = ReserveInfo.katakanaError(
                  '出張者の名字(カナ)',
                  t.lastNameKana || ''
                );
              }
            } else {
              errors.travelerFirstNameRomen = ReserveInfo.requiredError(
                '出張者の名前(ローマ字)',
                t.firstNameRoman || undefined
              );
              errors.travelerLastNameRomen = ReserveInfo.requiredError(
                '出張者の名字(ローマ字)',
                t.lastNameRoman || undefined
              );
              errors.travelerFirstNameKana = ReserveInfo.requiredError(
                '出張者の名前(カナ)',
                t.firstNameKana || undefined
              );
              errors.travelerLastNameKana = ReserveInfo.requiredError(
                '出張者の名字(カナ)',
                t.lastNameKana || undefined
              );
              if (_.isEmpty(errors.travelerFirstNameRomen) && _.isEmpty(errors.travelerLastNameRomen)) {
                errors.travelerFirstNameRomen = ReserveInfo.alphabetError(
                  '出張者の名前(ローマ字)',
                  t.firstNameRoman || ''
                );
                errors.travelerLastNameRomen = ReserveInfo.alphabetError(
                  '出張者の名字(ローマ字)',
                  t.lastNameRoman || ''
                );
              }
              if (_.isEmpty(errors.travelerFirstNameKana) && _.isEmpty(errors.travelerLastNameKana)) {
                errors.travelerFirstNameKana = ReserveInfo.katakanaError(
                  '出張者の名前(カナ)',
                  t.firstNameKana || ''
                );
                errors.travelerLastNameKana = ReserveInfo.katakanaError(
                  '出張者の名字(カナ)',
                  t.lastNameKana || ''
                );
              }
              if (_.isEmpty(errors.travelerFirstNameKana) && _.isEmpty(errors.travelerLastNameKana)) {
                errors.travelerFirstName =
                  ReserveInfo.katakanaError('搭乗者氏名', t.flightLastNameKana || '') ||
                  ReserveInfo.katakanaError('搭乗者氏名', t.flightFirstNameKana || '');
              }
            }
            break;
          default:
        }
        if (
          this.user.organization.exicUsageMode === 'card_based' &&
          t.type !== 'companion' &&
          t.shinkansenTicketType === 'ex' &&
          (!t.exicCardId || String(t.exicCardId) === '')
        ) {
          errors[`travelerExicCardId_${i}`] = 'EXIC会員IDを設定してください';
        }
      }
      if (this.reservingTrip.isFlightCustomerInfoRequired) {
        if (this.reservingTrip.isForeignFlightCustomerInfoRequired) {
          errors[`flightCustomerNameKana_${i}`] =
            ReserveInfo.requiredError('搭乗者氏名(カナ)', t.flightLastNameKana || undefined) ||
            ReserveInfo.requiredError('搭乗者氏名(カナ)', t.flightFirstNameKana || undefined);
          if (_.isEmpty(errors[`flightCustomerNameKana_${i}`])) {
            errors[`flightCustomerNameKana_${i}`] =
              ReserveInfo.katakanaError('搭乗者氏名(カナ)', t.flightLastNameKana || '') ||
              ReserveInfo.katakanaError('搭乗者氏名(カナ)', t.flightFirstNameKana || '');
          }
          errors[`flightCustomerName_${i}`] =
            ReserveInfo.requiredError('搭乗者氏名(ローマ字)', t.flightLastNameRoman || undefined) ||
            ReserveInfo.requiredError('搭乗者氏名(ローマ字)', t.flightFirstNameRoman || undefined);
          if (_.isEmpty(errors[`flightCustomerName_${i}`])) {
            errors[`flightCustomerName_${i}`] =
              ReserveInfo.alphabetError('搭乗者氏名(ローマ字)', t.flightLastNameRoman || '') ||
              ReserveInfo.alphabetError('搭乗者氏名(ローマ字)', t.flightFirstNameRoman || '') ||
              ReserveInfo.alphabetErrorAllowEmpty('搭乗者氏名(ローマ字)', t.flightMiddleNameRoman || '');
          }
        } else {
          errors[`flightCustomerName_${i}`] =
            ReserveInfo.requiredError('搭乗者氏名(カナ)', t.flightLastNameKana || undefined) ||
            ReserveInfo.requiredError('搭乗者氏名(カナ)', t.flightFirstNameKana || undefined);
          if (_.isEmpty(errors[`flightCustomerName_${i}`])) {
            errors[`flightCustomerName_${i}`] =
              ReserveInfo.katakanaError('搭乗者氏名(カナ)', t.flightLastNameKana || '') ||
              ReserveInfo.katakanaError('搭乗者氏名(カナ)', t.flightFirstNameKana || '');
          }
          errors[`flightCustomerNameRoman_${i}`] =
            ReserveInfo.requiredError('搭乗者氏名(ローマ字)', t.flightLastNameRoman || undefined) ||
            ReserveInfo.requiredError('搭乗者氏名(ローマ字)', t.flightFirstNameRoman || undefined);
          if (_.isEmpty(errors[`flightCustomerName_${i}`])) {
            errors[`flightCustomerNameRoman_${i}`] =
              ReserveInfo.alphabetError('搭乗者氏名(ローマ字)', t.flightLastNameRoman || '') ||
              ReserveInfo.alphabetError('搭乗者氏名(ローマ字)', t.flightFirstNameRoman || '') ||
              ReserveInfo.alphabetErrorAllowEmpty('搭乗者氏名(ローマ字)', t.flightMiddleNameRoman || '');
          }
        }
        if (t.type === 'companion') {
          errors[`travelerBirthday_${i}`] = ReserveInfo.flightBirthdayError(
            '出張者の誕生日',
            t.flightBirthday?.format('YYYY-MM-DD') || ''
          );
        }
        errors[`flightCustomerBirthday_${i}`] = ReserveInfo.flightBirthdayError(
          '搭乗者の誕生日',
          t.flightBirthday?.format('YYYY-MM-DD') || ''
        );
        errors[`flightCustomerTel_${i}`] = ReserveInfo.requiredError(
          '搭乗者の緊急連絡先',
          toDomesticTel(t.flightTel || '')
        );
        if (!errors[`flightCustomerTel_${i}`]) {
          errors[`flightCustomerTel_${i}`] = ReserveInfo.telNumberError(
            '搭乗者の緊急連絡先',
            toDomesticTel(t.flightTel || '')
          );
        }
        if (this.reservingTrip.isForeignFlightCustomerInfoRequired) {
          errors[`passportNumber_${i}`] = ReserveInfo.requiredError(
            'パスポート番号',
            t.passportNumber || undefined
          );
          errors[`passportExpire_${i}`] = ReserveInfo.requiredError(
            'パスポート有効期限',
            t.passportExpire || undefined
          );
          if (_.isEmpty(errors[`passportExpire_${i}`])) {
            errors[`passportExpire_${i}`] = ReserveInfo.expiredError('パスポート有効期限', t.passportExpire || '');
          }
          errors[`passportIssuedCoutry_${i}`] = ReserveInfo.requiredError(
            'パスポート発行国',
            t.passportIssuedCountry || undefined
          );
        }
        if (
          !this.reservingTrip.isPackage &&
          this.reservingTrip.travelers.filter(traveler => traveler.type === 'self').length > 1
        ) {
          errors.selfSelection = '航空券が含まれる場合、「自分の手配をする」は1人のみ選択可能です。';
        }
      }
      if (this.reservingTrip.items.some(item => item?.isFlight)) {
        errors[`flightCustomerGender_${i}`] = ReserveInfo.requiredError(
          '性別',
          t.flightGender || undefined,
          'を選択してください'
        );
      }
    });

    if (this.isRentalCarRequired) {
      _.map(this.reservingTrip.rentalCars, (rentalCar, j) => {
        errors[`departureDate_${j}`] = this.limitError('出発時間', rentalCar.departureTime.format('YYYY-MM-DD'));
        if (_.isEmpty(rentalCar.departurePlace)) {
          errors[`departurePlace_${j}`] = ReserveInfo.requiredError('出発場所', rentalCar.departurePlace);
        }
        if (rentalCar.returnPlace !== 'current' && _.isEmpty(rentalCar.returnPlaceName)) {
          errors[`returnPlace_${j}`] = ReserveInfo.requiredError('返却場所', rentalCar.returnPlaceName);
        }
        if (rentalCar.carType === 'other' && _.isEmpty(rentalCar.carTypeOther)) {
          errors[`carType_${j}`] = ReserveInfo.requiredError('車種・クラス', rentalCar.carTypeOther);
        }
        errors[`rcLastNameKana_${j}`] = ReserveInfo.requiredError('利用者氏名（カナ）', rentalCar.lastNameKana);
        errors[`rcFirstNameKana_${j}`] = ReserveInfo.requiredError('利用者氏名（カナ）', rentalCar.firstNameKana);
        if (_.isEmpty(errors[`rcLastNameKana_${j}`]) && _.isEmpty(errors[`rcFirstNameKana_${j}`])) {
          errors[`rcLastNameKana_${j}`] = ReserveInfo.katakanaError('利用者氏名（カナ）', rentalCar.lastNameKana);
          errors[`rcFirstNameKana_${j}`] = ReserveInfo.katakanaError(
            '利用者氏名（カナ）',
            rentalCar.firstNameKana
          );
        }
        errors[`rcTel_${j}`] = ReserveInfo.requiredError('運転者電話番号', toDomesticTel(rentalCar.driverTel));
      });
    }

    if (
      this.isDestinationBusinessTripShown &&
      (this.isApprovalRequired || this.isDestinationBusinessTripRequired)
    ) {
      errors.area = ReserveInfo.requiredError('出張先', this.finalDestination);
      if (!errors.area) {
        errors.area = ReserveInfo.surrogatePairError('出張先', this.finalDestination);
      }
    }
    if (this.finalDestination.length > 255) {
      errors.finalDestinationLength = '出張先は255文字まで入力できます。';
    }

    if (this.isPurposeBusinessTripShown && (this.isApprovalRequired || this.isPurposeBusinessTripRequired)) {
      errors.purpose = ReserveInfo.requiredError('出張の目的', this.purpose);
      if (!errors.purpose) {
        errors.purpose = ReserveInfo.surrogatePairError('出張の目的', this.purpose);
      }
    }

    if (!this.user.organization?.freeeWorkflow && this.isApprovalRequired && this.workflowStyle === 'trip') {
      if (this.approveStages.length === 0) {
        errors.approveStages = 'ワークフロールートに空欄があります。承認者を登録してください。';
      }
      _.each(this.approveStages, stage => {
        _.each(stage.approvers, approver => {
          if (_.isNil(approver)) {
            errors.approveStages = 'ワークフロールートに空欄があります。承認者を登録してください。';
          }
        });
      });
    }

    if (
      this.user.organization?.reserveRequestMailNotificationType === 'selectable_user' &&
      (_.isNil(this.notifiedUsers) || this.notifiedUsers?.length === 0)
    ) {
      errors.notifiedUsers = '出張内容を確認するユーザを1名以上設定してください。';
    }

    if (this.expensesAccountTypeAvailable && this.isExpensesAccountTypeRequired) {
      errors.expensesAccountType = ReserveInfo.requireExpensesMappingError('勘定科目', expensesMapping);
    }

    if (this.isEnabledAutoReserveRakutenHotel) {
      _.map(this.hotels, (hotel, i) => {
        if (hotel.type !== 'new_rakuten') {
          return;
        }
        const hotelNames = _.map(hotel.rooms, room => `${room.hotelLastName}${room.hotelFirstName}`);
        if (_.uniq(hotelNames).length !== hotelNames.length) {
          errors[`autoReserveRakutenHotel_${i}`] =
            'このホテルは同じ代表者名で復数の部屋を予約することができません。\n宿泊者名を全て違う値に変更してください。';
        }
      });
    }

    _.map(this.hotels, (hotel, i) => {
      if (hotel.type !== 'agoda') {
        return;
      }
      if (!hotel.rooms || hotel.rooms.length <= 1) {
        return;
      }
      const hotelNames = _.map(hotel.rooms, room => `${room.hotelLastName}${room.hotelFirstName}`);
      if (_.uniq(hotelNames).length !== hotelNames.length) {
        errors[`agodaHotelDuplicate_${i}`] =
          'このホテルは同じ代表者名で復数の部屋を予約することができません。宿泊者名を全て違う値に変更してください。';
      }
    });

    this.approveItems.forEach(r => {
      if (
        (r.requiredType !== 'optional' && r.requiredType !== 'required_with_workflow' && r.requiredType !== '') ||
        this.isApproveItemRequiredWithWorkflow(r.requiredType) === true
      ) {
        errors[`appleveItem_${r.id}`] = ReserveInfo.requiredError(
          r.userDisplayName,
          this.approveItemValues.get(r.id)
        );
      }
    });
    errors.card = this.cardError;
    errors.organization = this.organizationErrors;

    return compactObject(errors);
  }
}
